/**
 * OnboardingStudio — the REAL editing surface, embedded where a wizard needs
 * one: Brand Setup's Review › **Adjust on the video** (spec §2.2/§3) and the
 * classic Onboard's Design step.
 *
 * Same Player + ClipComposition + canvas overlay as the per-clip Studio, but
 * channel-LESS and server-less: inputProps come from the wizard (colors,
 * fonts, logo) over the committed sample clip, and the edit journal lives in
 * memory + a localStorage draft. The controls are the **Focus editor** — one
 * panel of essentials beside the preview, the whole inspector one "More
 * options" click away — so the operator learns ONE editor, here and in the
 * Studio.
 *
 * ---------------------------------------------------------------------------
 * The RESEED, and the defect it fixes (spec §2.3)
 * ---------------------------------------------------------------------------
 * The journal opens on ONE entry — "Your choices so far" — built from the
 * wizard's step choices. It used to be seeded only when there was no draft,
 * with "a saved draft always wins". That made the seed self-persisting: open
 * Adjust once, go back and change a step, and the step change was silently
 * discarded by the stale entry 0 on the way back in.
 *
 * So the seed's CONTENT HASH rides with the draft. On mount, if the saved
 * entry 0 is the seed and the hash no longer matches, entry 0 is rebuilt from
 * the current choices and entries 1..n — the operator's own fine-tune work —
 * are kept on top of it. Read once, at mount: a reactive reseed would rebuild
 * the journal underneath the hands that are editing it.
 */
import { Pause, Play } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Player, type PlayerRef } from "@remotion/player";
import { ClipComposition } from "../../../src/remotion/ClipComposition.tsx";
import {
  appendEntry,
  applyEditState,
  baselineBrand,
  baselineLayout,
  collapseLayoutBrand,
  undoLast,
  type ClipEditState,
  type EditPatch,
  type EditSource,
} from "../../../src/lib/clipEditState.ts";
import {
  fineTuneCount,
  isSeedEntry,
  openingJournal,
  parseDesignDraft,
} from "../lib/brandSetup/adjustSeed";
import { CANVAS_HEIGHT, CANVAS_WIDTH, FPS, type ClipProps } from "../../../src/remotion/types.ts";
import { listStylePresets, type CustomStylePreset } from "../api";
import { StudioCanvasOverlay } from "../components/StudioCanvasOverlay";
import type { StudioInspectorProps } from "../components/StudioInspector";
import { FocusEditor } from "../components/focus/FocusEditor";
import { FocusPanel, type FocusPanelProps } from "../components/focus/FocusPanel";
import { iconBtnStyle } from "../components/styles";
import { type StudioSelection } from "../lib/studioTypes";
import { useMinWidth } from "../lib/useIsMobile";
import { SAMPLE_DURATION_SEC } from "../lib/onboardingSample";

const DRAFT_PREFIX = "ql-onboard-design:";


/** Height the stage falls back to before the host measures its chrome. */
const FALLBACK_STAGE_H = 560;

export interface OnboardingStudioProps {
  /** Draft key — the wizard slug (or "new") so parallel drafts don't clash. */
  draftKey: string;
  /** Baseline props from the wizard (sample clip + collected brand). */
  inputProps: ClipProps;
  /**
   * Called on every journal change with the collapsed design the USER made
   * here — entries 1..n, never the seed.
   *
   * The seed is the wizard's own `designPatch` handed back to it; re-claiming
   * those paths would mark fields the operator never touched as "mine", and a
   * later re-read of the brand could then no longer replace them. Only the
   * fine-tune work is the user's, so only the fine-tune work is lifted.
   */
  onDesignChange: (design: EditPatch, steps: number) => void;
  /**
   * What the STEPS decided (`state.designPatch`) — entry 0, rebuilt from this
   * on every mount, so a step changed since the last visit lands here without
   * anything having to notice.
   */
  stepPatch?: EditPatch;
  /**
   * What the operator fine-tuned last time (`state.adjustLift`). The saved
   * journal is replayed on top of entry 0, PRUNED to these paths — a key a
   * later step has answered is no longer the lift's to re-apply.
   */
  lift?: EditPatch;
  /** px the stage may occupy. The host measures its own chrome. */
  stageHeight?: number;
}



export function OnboardingStudio(props: OnboardingStudioProps): React.JSX.Element {
  const { draftKey, inputProps, onDesignChange, stepPatch, lift, stageHeight } = props;
  const storageKey = DRAFT_PREFIX + draftKey;
  const wide = useMinWidth(900);
  // Read ONCE, at mount: a reactive reseed would rebuild the journal under the
  // hands that are editing it. `adjustSeed.ts` owns every rule (and its tests).
  const [editState, setEditState] = useState<ClipEditState>(() => {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(storageKey);
    } catch { /* private mode — open on the wizard's own choices */ }
    return openingJournal(parseDesignDraft(raw), stepPatch, lift);
  });
  const [livePatch, setLivePatch] = useState<EditPatch | null>(null);
  const [selection, setSelection] = useState<StudioSelection>({ el: "caption", cueIdx: null });
  const [playing, setPlaying] = useState(false);
  const [frame, setFrame] = useState(0);
  const playerRef = useRef<PlayerRef | null>(null);

  /** True while entry 0 is the wizard's seed — it is neither the user's work
   *  nor theirs to undo. */
  const seeded = isSeedEntry(editState.journal[0]);

  // Persist the journal + surface the USER's half of it upward on every change.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ state: editState }));
    } catch { /* draft only */ }
    const mine: ClipEditState = { version: 1, journal: editState.journal.filter((e) => !isSeedEntry(e)) };
    onDesignChange(collapseLayoutBrand(mine), fineTuneCount(editState));
  }, [editState, storageKey, onDesignChange]);

  const commit = useCallback((patch: EditPatch, label: string, source: EditSource): void => {
    setLivePatch(null);
    setEditState((s) => appendEntry(s, { ts: Date.now(), source, label, patch }));
  }, []);

  const effective = useMemo(
    () => applyEditState(inputProps, editState, livePatch),
    [inputProps, editState, livePatch],
  );
  const layoutView = useMemo(() => baselineLayout(effective), [effective]);
  const brandView = useMemo(() => baselineBrand(effective), [effective]);
  const baselineView = useMemo(
    () => ({ ...baselineLayout(inputProps), brand: baselineBrand(inputProps) }),
    [inputProps],
  );
  const fontFamilies = useMemo(
    () => [...new Set((inputProps.fontFaces ?? []).map((f) => f.family))],
    [inputProps],
  );
  const [customPresets, setCustomPresets] = useState<CustomStylePreset[]>([]);
  useEffect(() => {
    let live = true;
    listStylePresets()
      .then((p) => { if (live) setCustomPresets(p.filter((preset) => {
        const b = preset.patch.brand;
        return (["captionFont", "punchFont", "speakerFont"] as const).every(
          (role) => !b?.[role] || fontFamilies.includes(b[role]!),
        );
      })); })
      .catch(() => { /* additive */ });
    return () => { live = false; };
  }, [fontFamilies]);

  // Track play state + the playhead's ACTIVE CUE (the overlay's cue-targeted
  // behavior must follow what's on screen, not stick to cue 1).
  const [activeCueIdx, setActiveCueIdx] = useState(0);
  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    const onPlay = (): void => setPlaying(true);
    const onPause = (): void => setPlaying(false);
    const onFrame = (e: { detail: { frame: number } }): void => {
      setFrame(e.detail.frame);
      const ms = (e.detail.frame / FPS) * 1000;
      const idx = effective.cues.findIndex((c) => ms >= c.startMs && ms < c.endMs);
      if (idx >= 0) setActiveCueIdx(idx);
    };
    p.addEventListener("play", onPlay);
    p.addEventListener("pause", onPause);
    p.addEventListener("frameupdate", onFrame as never);
    return () => {
      p.removeEventListener("play", onPlay);
      p.removeEventListener("pause", onPause);
      p.removeEventListener("frameupdate", onFrame as never);
    };
  }, [effective.cues]);

  // No `stageHeight` from the host (the classic Onboard) → take what is left
  // of the viewport under wherever this landed, measured rather than guessed.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [ownHeight, setOwnHeight] = useState(FALLBACK_STAGE_H);
  useLayoutEffect(() => {
    if (stageHeight !== undefined) return;
    const measure = (): void => {
      const top = rootRef.current?.getBoundingClientRect().top ?? 0;
      setOwnHeight(Math.max(360, Math.round(window.innerHeight - top - 24)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [stageHeight]);

  const undoable = editState.journal.length > (seeded ? 1 : 0);
  const lastEntry = editState.journal[editState.journal.length - 1];

  const inspectorProps: StudioInspectorProps = {
    selection,
    onSelect: setSelection,
    // baselineLayout materializes only the geometry it can default;
    // `overlays` has no baseline (absent = none), so it rides along from the
    // effective props. Passing it is what makes the Overlays editor
    // non-destructive — it writes the WHOLE list, so it has to SEE the whole
    // list. `?? []` is load-bearing: it says "I track overlays, there are
    // none", which is what un-hides the editor.
    layout: { ...layoutView, overlays: effective.layout?.overlays ?? [] },
    brand: brandView,
    speakerCardLive: effective.speakers.some((s) => s.live != null),
    cue: selection.cueIdx != null ? effective.cues[selection.cueIdx] ?? null : null,
    clipName: "sample",
    durationSec: SAMPLE_DURATION_SEC,
    cueCount: effective.cues.length,
    journal: editState.journal,
    onPreview: setLivePatch,
    onCommit: (patch, label) => commit(patch, label, "inspector"),
    channelSlug: null,
    fontFamilies,
    onUploadFont: null,
    customPresets,
    onSavePreset: async () => "Save presets from a clip's Studio — the design here saves with the channel.",
    onDeletePreset: async () => {},
    baseline: baselineView,
  };

  const panel: FocusPanelProps = {
    host: "brand-setup",
    inspector: inspectorProps,
    selection,
    onSelect: setSelection,
    cues: effective.cues,
    onSelectCue: (i) => {
      const cue = effective.cues[i];
      if (cue) playerRef.current?.seekTo(Math.round((cue.startMs / 1000) * FPS) + 1);
      setSelection({ el: "caption", cueIdx: i });
    },
    // Never offers to undo the wizard's own choices — those are changed on
    // their steps, and popping one here would look like a bug from the rail.
    undoLabel: undoable ? lastEntry?.label ?? null : null,
    onUndo: () => setEditState((s) => (fineTuneCount(s) > 0 ? undoLast(s) : s)),
  };

  const preview = (height: number): React.JSX.Element => (
    <div
      style={{
        position: "relative",
        aspectRatio: "9 / 16",
        height,
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,.08)",
        background: "#000",
        boxShadow: "0 12px 40px rgba(0,0,0,.45)",
      }}
    >
      <Player
        ref={playerRef}
        component={ClipComposition}
        inputProps={effective}
        durationInFrames={SAMPLE_DURATION_SEC * FPS}
        fps={FPS}
        compositionWidth={CANVAS_WIDTH}
        compositionHeight={CANVAS_HEIGHT}
        style={{ width: "100%", height: "100%" }}
        clickToPlay={false}
        loop
        acknowledgeRemotionLicense
      />
      <StudioCanvasOverlay
        effective={effective}
        selection={selection}
        onSelect={setSelection}
        onLive={setLivePatch}
        onCommit={(patch, label) => commit(patch, label, "canvas")}
        activeCueIdx={activeCueIdx}
      />
    </div>
  );

  const transport = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, minWidth: 0 }}>
      <button
        type="button"
        onClick={() => (playing ? playerRef.current?.pause() : playerRef.current?.play())}
        style={{ ...iconBtnStyle, width: 40, height: 40, flexShrink: 0 }}
        title={playing ? "Pause" : "Play the sample"}
      >
        {playing ? <Pause size={16} strokeWidth={2} /> : <Play size={16} strokeWidth={2} />}
      </button>
      <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 12.5, color: "var(--ql-fg-muted)" }}>
        {clock(frame / FPS)} / {clock(SAMPLE_DURATION_SEC)}
      </span>
      <span style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", lineHeight: 1.4 }}>
        Click anything on the video to change it.
      </span>
    </div>
  );

  // A phone has no room for a side-by-side, so the panel stacks BELOW the
  // preview — the same shape the Studio takes at the same width (spec §4).
  return (
    <div ref={rootRef} style={{ minWidth: 0 }}>
      {wide ? (
        <FocusEditor
          panel={panel}
          stageHeight={stageHeight ?? ownHeight}
          render={(h) => preview(h)}
          transport={transport}
        />
      ) : (
        <div style={{ display: "grid", gap: 12, justifyItems: "center", minWidth: 0 }}>
          {preview(Math.min(420, Math.round((stageHeight ?? ownHeight) * 0.52)))}
          {transport}
          <div style={{ width: "100%", height: "min(60vh, 480px)" }}>
            <FocusPanel {...panel} width="100%" />
          </div>
        </div>
      )}
    </div>
  );
}

function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `0:${String(s).padStart(2, "0")}`;
}

/** Clear the design draft after a successful channel create/save. */
export function clearOnboardingDesignDraft(draftKey: string): void {
  try {
    localStorage.removeItem(DRAFT_PREFIX + draftKey);
  } catch { /* best effort */ }
}
